/**
 * Voice transport: microphone -> OUR socket.io relay -> tool-call proposals.
 *
 * The browser no longer talks to a speech vendor. It streams PCM to our own
 * server over the socket.io connection the app already holds, and the server
 * owns the upstream Muse Voice Transcribe socket, the API key, the ledger row
 * and the parse. What comes back is a small, stable vocabulary —
 * `voice:ready`, `voice:partial`, `voice:final`, `voice:command`,
 * `voice:error`, `voice:stopped` — and this file's whole job is to turn that
 * into the `onEvent(type, payload)` contract the modal already consumes.
 *
 * Why the key moved: an ephemeral vendor token in a browser is a credential in
 * a browser. Keeping it server-side also puts endpointing, the session ceiling
 * and the billing ledger on the side of the wire that cannot be edited by
 * whoever is holding the phone.
 *
 * THE SAFETY PROPERTY, unchanged and stated once: nothing in this file applies
 * anything. A parsed command produces a PREVIEW and an `onEvent("proposal")`.
 * The caller shows it to a person, and only the modal's explicit confirmation
 * path can reach apply. Completed transcripts may request that path through a
 * strict whole-utterance whitelist; this module still never writes inventory.
 *
 * Audio is 24 kHz PCM16 mono, produced in an AudioWorklet and sent as binary
 * ArrayBuffers — socket.io carries those natively, so there is no base64 tax on
 * a stream that runs for the whole session.
 *
 * The event mapping itself lives in `voiceRelayEvents.js`, which is pure and
 * has a node test. This file is the part that owns hardware and timers.
 */

import socket from "../socket";
import { createGate, outboundFrame } from "./voiceHoldGate";
import { createSpeechMeter } from "./voiceSpeechMeter";
import {
  createChunkAssembler,
  int16ToFloat32,
  SAMPLE_RATE_HZ,
  speakEndDecision,
  speakEndRearmMs,
} from "./voiceSpeakChunks";
import {
  classifyToolCall,
  mapRelayEvent,
  relayError,
} from "./voiceRelayEvents";
import {
  confirmWindowMs,
  createTimerSet,
  delayUntilNext,
  streamIdleAllowed,
  STOP_REASON_FOR_KIND,
  STREAM_TIMER_KINDS,
  VOICE_STREAM_IDLE_MS,
} from "./voiceTimers";

// Re-exported rather than moved outright: they were part of this module's
// public surface before the transport swap, and the phrase lists belong with
// the pure classifier that the node test drives.
export {
  normalizeVoiceConfirmation,
  classifyVoiceConfirmation,
} from "./voiceRelayEvents";

// One render quantum per message: 128 samples, ~5.3 ms at 24 kHz. Also carries
// an RMS level, computed there rather than on the main thread so the meter
// cannot cost frames.
const WORKLET_SOURCE = `
  class PcmProcessor extends AudioWorkletProcessor {
    process(inputs) {
      const channel = inputs[0][0];
      if (channel) {
        const pcm = new Int16Array(channel.length);
        let sumSquares = 0;
        for (let i = 0; i < channel.length; i++) {
          const clamped = Math.max(-1, Math.min(1, channel[i]));
          sumSquares += clamped * clamped;
          pcm[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
        }
        const rms = Math.sqrt(sumSquares / channel.length);
        this.port.postMessage({ buffer: pcm.buffer, rms }, [pcm.buffer]);
      }
      return true;
    }
  }
  registerProcessor('sa-pcm', PcmProcessor);
`;

// Hands-free is the default: the SERVER endpoints, so one open stream carries a
// whole conversation and each spoken turn comes back as its own `voice:final`.
// Push-to-talk exists for a future hold-to-talk button and is the only mode in
// which the client decides where an utterance ends, via `endUtterance()`.
export const VOICE_MODE_HANDS_FREE = "hands_free";
export const VOICE_MODE_PUSH_TO_TALK = "push_to_talk";

// A session that has gone quiet is still billing. Server-side budgets now
// backstop this, but the client keeps its own copy: the server's ceiling
// protects the bill, and this one protects the user from a modal that sits
// open with a live microphone in it.
const SILENCE_TIMEOUT_MS = 60000;

// A rendered question is normally one the user must answer. Reading it deserves
// more than an endpoint's worth of silence but not a whole billed minute.
const ANSWER_WINDOW_MS = 15 * 1000;

// A rendered proposal keeps the paid session open just long enough for one
// explicit spoken decision. It starts after browser TTS finishes (or at render
// when TTS is unavailable/off), never restarts on noise, and remains inside the
// untouched 60s silence and 90s absolute backstops.
//
// The number itself moved to `voiceTimers.TIMER_DEFAULTS_MS.confirm` (still
// 12 s) because the window is no longer one length: `confirmWindowMs` widens it
// to a 20 s read-time floor when spoken replies are off, which is the case that
// used to give someone reading a delete card a spoken decision's worth of time.
// A local copy of the constant here would be a second source for one number.

// No recognised speech means there is no live reason to keep a paid session
// open. This one-shot cap begins when billing begins; the first partial or
// final transcript cancels it permanently for this session.
const NO_SPEECH_TIMEOUT_MS = 20 * 1000;

// Local voice activity, used for the "speaking" indicator and nothing else.
//
// It used to end the session on trailing silence. It must not any more: in
// hands-free the SERVER decides where an utterance ends, and a client that cut
// the stream three seconds after the user stopped talking would end the whole
// conversation at the end of its first sentence. What survives is the part the
// server cannot do — the modal's `listening {speaking:true}` flourish, which
// previously came from vendor VAD events the relay protocol does not have.
//
// The threshold stays deliberately low: this is "no speech", not "quiet room".
const ENDPOINT_RMS_THRESHOLD = 0.012;
// Trailing silence that releases the indicator, so the next utterance can raise
// it again. Same constant, same meaning — "the speaker has finished" — with the
// stop removed.
const ENDPOINT_SILENCE_MS = 3000;

// 80 ms per send: 1,920 samples, 3,840 bytes. The worklet emits a 128-sample
// quantum every ~5.3 ms, and one socket.io message per quantum is roughly 190
// messages a second for the entire session. Coalescing to the server's own
// frame size costs nothing in latency the user can perceive and keeps the
// message rate somewhere sane.
const AUDIO_FRAME_SAMPLES = 1920;

// Frames captured before the relay is ready are HELD, not dropped.
//
// The browser marks the microphone live the instant permission is granted, so
// the user starts talking then — but the relay still has to connect, check the
// lab's allowance, write a ledger row and open the upstream socket. Every word
// spoken in that gap used to be lost, because the audio graph was not yet
// connected to anything.
//
// Bounded at ~3 s: these are 80 ms frames now, not ~5 ms ones, and the server
// drops a stream whose queue exceeds 5 s of audio. A pre-buffer that flushed
// more than that would trip the very backlog guard it was trying to beat.
const PREBUFFER_MAX_FRAMES = 37;

// Backstop for a session that is never quiet — a noisy lab, a stuck stream. The
// server enforces its own 90 s ceiling on the bill; this one exists so the UI
// agrees with it rather than waiting to be told.
const MAX_SESSION_MS = 90 * 1000;

// TWO LIFETIMES, and this is the line between them.
//
// The MICROPHONE GRAPH is free. It costs Chrome's recording indicator and
// nothing else, and it lives as long as the panel is open (the pre-open, FIX-F5).
// The RELAY STREAM is billed by Muse at $0.003 per audio minute for as long as
// it is open, silence included — so it lives only around an interaction: opened
// by the tap, closed again after `VOICE_STREAM_IDLE_MS` of nothing happening
// when nothing is owed to the person looking at the panel.
//
// `streamIdleAllowed` in `voiceTimers.js` decides "owed"; this file owns the
// timer that asks it and the teardown that answers it. The number itself is in
// the same module as the other five, because a duration on one side of a screen
// and its meaning on the other is how two of them drifted before.

// How long an outstanding turn may hold the stream open.
//
// A `voice:stop` cancels a parse the server is still running and never emits
// the proposal, so a stream torn down between a final transcript and its
// `voice:command` loses the command outright. The hold is bounded because a
// wait that never ends is a stream that never closes: the server's own parse
// ceiling is 15 s (`codexParser.js` DEFAULT_TIMEOUT_MS), so 20 s outlasts every
// answer it can give while staying well inside the 60 s silence backstop.
const PARSE_GRACE_MS = 20 * 1000;

// The app socket is normally already connected — App.vue connects it on login —
// so this only covers a reconnect racing the modal opening.
const SOCKET_CONNECT_TIMEOUT_MS = 5000;

// `voice:start` to `voice:ready` covers the allowance check, a ledger write and
// the upstream handshake. Bounded because a promise that never settles leaves a
// live microphone with nothing owning it, which is the failure this whole file
// is careful about.
const RELAY_READY_TIMEOUT_MS = 10 * 1000;

export default class VoiceSession {
  /**
   * @param {object} options
   * @param {object} [options.credentials] resolved mint result. Since the relay
   *   needs no vendor token this now carries only the recognition context —
   *   `keyterms`/`keywords` and `screenPuck` — which is how those reach the
   *   relay without the modal having to hand them over separately.
   * @param {function} options.onEvent (type, payload) => void
   * @param {string} [options.mode] `hands_free` (default) or `push_to_talk`.
   * @param {boolean} [options.gateOpen] hold-to-talk constructs with `false`,
   *   so the session opens with its audio gate shut. Defaults to open in
   *   EVERY mode, `push_to_talk` included.
   */
  constructor({
    credentials,
    onEvent,
    mode,
    keywords,
    screenPuck,
    gateOpen,
    streamIdleMs,
    timing,
  } = {}) {
    this.credentials = credentials || null;
    this.onEvent = onEvent || (() => {});
    // The dock's `TimingLedger`, or null. This session STAMPS four of its marks
    // and reads none of them: the two ends of the capture wait and the two ends
    // of a relay read-back are facts only this file can observe — the dock sees
    // them as events, one dispatch later, and the point of a stopwatch is to
    // start it where the thing happens. Optional and always called through `?.`,
    // because the warm pre-open and every probe construct a session without one.
    //
    // MARKS ONLY. Nothing here reads a span, decides anything on one, or sends
    // one: the ledger's owner does all three.
    this.timing = timing || null;
    this.mode =
      mode === VOICE_MODE_PUSH_TO_TALK
        ? VOICE_MODE_PUSH_TO_TALK
        : VOICE_MODE_HANDS_FREE;
    // Hold-to-talk is a gate on THIS one hands-free session rather than a
    // session per press, so the button costs no handshake and no ledger row.
    //
    // Open unless the caller asks otherwise, and deliberately INDEPENDENT of
    // `mode`. Hands-free therefore behaves byte for byte as it did, the dock
    // constructs with `gateOpen: false` when the mic mode is hold-to-talk and
    // drives it with `setGate`, and `push_to_talk` — which nothing exercises
    // yet — is not quietly handed a shut gate that would make `endUtterance()`
    // send an utterance of pure silence.
    //
    // This DEVIATES from P1.8 on purpose: the brief specified
    // `open: mode !== 'push_to_talk' && gateOpen !== false`, and the dropped
    // term is the one that would shut the gate on push-to-talk by default. The
    // deviation is recorded here rather than pinned by a test, so restoring the
    // term stays a one-line decision rather than a test failure.
    this.gate = createGate({ open: gateOpen !== false });
    // Constructor options win over the mint result when both are supplied.
    this.keywordsOption = Array.isArray(keywords) ? keywords : null;
    this.screenPuckOption = screenPuck === undefined ? null : screenPuck;

    this.audioContext = null;
    this.micStream = null;
    this.workletNode = null;
    this.sourceNode = null;
    // The microphone graph is open and capturing. Set once `startStreamingAudio`
    // has run, cleared when the tracks are released, and the reason a session
    // that was pre-opened never asks for the device a second time.
    this.micReady = false;
    // The in-flight open, so two callers — the dock pre-opening on mount and
    // the press that starts a relay a moment later — share ONE `getUserMedia`
    // rather than racing two permission round trips at the same device.
    this.micOpening = null;
    // `{label, settings, openMs}` from the granted track, kept so a caller
    // handed an already-open session can be told what it is holding.
    this.micInfo = null;
    // A relay session has been asked for. FALSE for a microphone opened ahead
    // of the press: the graph runs, the meter is honest, and every frame is
    // dropped rather than held, because nothing has been started to hold it for.
    //
    // It goes back to FALSE on every `closeStream`, which is the whole of "send
    // nothing to a stream that is not there": `sendAudio` reads it first and
    // returns, so a torn-down stream cannot be written to and cannot be queued
    // for either.
    this.relayRequested = false;

    // How long a stream may rest before it is closed. An option so a probe can
    // drive it in seconds rather than in minutes; the default is the one in
    // `voiceTimers.js`.
    this.streamIdleMs = Number.isFinite(streamIdleMs)
      ? streamIdleMs
      : VOICE_STREAM_IDLE_MS;

    // Whether a spoken reply is being read back to this user. It decides
    // whether a pending card holds the stream open: with replies ON the panel
    // has promised "say confirm" and the word must be able to land, with them
    // OFF nothing has been promised and the card says to tap instead. Owned by
    // the dock's preference, pushed in rather than read out.
    this.repliesOn = true;

    // Seconds billed by streams that have already closed within THIS panel-open.
    // The footer's clock is this plus the stream that is running, so a person
    // watching it sees the effect of the teardown: the number stops moving.
    this.billedSecondsBefore = 0;

    // What the most recently closed stream ended on. The usage report names ONE
    // `voicesessions` row — the last stream's — and a session stopped after its
    // stream was already torn down has a clock reading zero, which would PATCH
    // a real number away. This is that number, kept.
    this.lastStreamSeconds = 0;

    // Audio in which somebody was actually TALKING — forwarded (gate open, not
    // muted, not held for a read-back) AND carrying energy — counted off the
    // worklet's own quanta. `spokenSamplesBefore` closes over the streams
    // already ended; the other is the stream running now, and it is what rides
    // out on that stream's `voice:stop` for the server to store beside the
    // billed seconds.
    this.spokenSamplesBefore = 0;
    this.streamSpokenSamples = 0;
    // What decides "spoken" for those counters. Energy, not permission: see
    // `voiceSpeechMeter.js`. It holds one number — audio since the last loud
    // quantum — and is reset whenever the run of captured audio breaks.
    this.speechMeter = createSpeechMeter();

    this.closed = false;
    // Set only while preview/apply HTTP is in flight. A rendered proposal must
    // forward audio again so finals can carry a spoken decision.
    this.muted = false;
    // Browser speech playback is a separate gate from decision/apply muting.
    // Frames are still metered while held, but are neither analysed nor sent
    // upstream, preventing speaker bleed from being transcribed back.
    this.playbackHeld = false;
    this.startedAt = null;
    // Kept for the tool-result bookkeeping `raiseProposal` still does. The
    // relay parses transcripts rather than streaming a call in two halves, so
    // this no longer has to hold a name and its arguments together.
    this.pendingCalls = {};
    this.outcome = { commandsPreviewed: 0, commandsApplied: 0, refusals: 0 };

    // ONE timer set and ONE `setTimeout`. The set holds absolute deadlines by
    // kind and answers "which is next"; `deadlineTimer` is aimed at that
    // answer and re-aimed by `reschedule()` after every arm or disarm. Five
    // independent timeouts used to end the session with no way to say which
    // had fired — this is the same policy with a name attached to it, and the
    // name is what reaches the screen.
    this.timers = createTimerSet();
    this.deadlineTimer = null;
    // `kind:deadlineAt` of the last `deadline` event, so a reschedule that
    // lands on the same answer stays silent instead of re-emitting per frame.
    this.lastDeadlineKey = null;
    this.confirmIntent = null;
    this.confirmWindowStarted = false;
    this.wrongConfirmPhraseEmitted = false;
    // Set by the pure mapper when a final was classified as a spoken decision,
    // so the relay's parse of that same turn can be dropped. The relay parses
    // EVERY final and has no idea a confirm window is open; "confirm" becoming
    // a command proposal is exactly the bug this closes.
    this.confirmationTurnKey = null;
    // True once a `voice:error` has been rendered, which changes how an
    // `error` stop is reported. See `mapStopReason`.
    this.sawError = false;

    // The read-back in progress, if any: its assembler, its scheduled buffer
    // sources and the timer that ends it. Null when nothing is being spoken.
    this.speaking = null;
    this.sayCounter = 0;

    // Audio held until `voice:ready`, and the coalescing accumulator.
    this.preBuffer = [];
    this.audioChunks = [];
    this.audioSamples = 0;

    // Relay lifecycle.
    this.relayLive = false;
    this.relayReady = false;
    this.relayHandlers = null;
    this.startSettled = false;
    this.readyResolve = null;
    this.readyReject = null;
    this.readyTimer = null;
    // Seconds of audio the SERVER says it forwarded. That is the bill basis, so
    // it wins over wall-clock time when the server reports it.
    this.reportedSeconds = null;

    // Local voice activity latch for the "speaking" indicator.
    this.heardSpeech = false;
    this.quietSince = null;

    // Bookkeeping the mapper maintains and the timers read.
    //
    // `sawTranscript` gates the never-spoke cap: a session with no recognised
    // speech has no live reason to stay open. `awaitingResponse` and
    // `turnHadCall` describe what the turn in flight produced; with endpointing
    // now server-side nothing cuts a session on them, but they remain the
    // session's record of the turn and the pure mapper's contract.
    this.sawTranscript = false;
    this.awaitingResponse = false;
    this.turnHadCall = false;

    // When the two outstanding waits began, so each can be BOUNDED. The mapper
    // sets `awaitingResponse` and the dock sets `muted`; neither carries a
    // clock, and a hold with no clock on it is a stream that never closes.
    this.awaitingSince = null;
    this.mutedSince = null;
  }

  /**
   * Seconds the STREAM RUNNING NOW has billed for.
   *
   * The server's forwarded-audio count wins when it has sent one: that is the
   * bill basis. Wall clock from `voice:ready` is the fallback, and it is zero
   * for a session whose stream is currently closed — which is the honest answer,
   * because a closed stream is not billing.
   */
  currentStreamSeconds() {
    if (this.reportedSeconds != null) return this.reportedSeconds;
    if (!this.startedAt) return 0;
    return Math.round((Date.now() - this.startedAt) / 1000);
  }

  /**
   * Seconds billed across every stream this panel-open has opened.
   *
   * One tap used to be one session and one number. Now a panel-open can carry
   * several streams — each opened by a tap and closed by the idle timer — and
   * the footer has to show the SUM or it would reset to 0:00 every time the
   * stream came back, which is exactly the moment the user is being shown that
   * the teardown worked.
   */
  elapsedSeconds() {
    return this.billedSecondsBefore + this.currentStreamSeconds();
  }

  /**
   * Seconds in which somebody was actually talking, this panel-open.
   *
   * Counted off the worklet's quanta rather than off wall time, and only for
   * the frames that both CARRIED THE ROOM — gate open, not muted, not held for
   * a read-back — and CARRIED A VOICE, which `voiceSpeechMeter.js` decides from
   * the worklet's own RMS with a 200 ms hangover so the gaps inside a sentence
   * are not scored as silence.
   *
   * The energy test is the whole difference from FIX-F11, which counted every
   * forwarded frame. Forwarded frames are also BILLED frames, so on that rule a
   * silent held microphone read as 100% spoken and the billed/spoken ratio this
   * number exists to expose sat at 1.00× in exactly the case it should have
   * been screaming about. It is the denominator of the thing the packet exists
   * to shrink — billed minutes against minutes anyone actually spoke into — and
   * a denominator that cannot exceed its numerator measures nothing.
   */
  spokenSeconds() {
    return (
      Math.round(
        ((this.spokenSamplesBefore + this.streamSpokenSamples) /
          SAMPLE_RATE_HZ) *
          10
      ) / 10
    );
  }

  /** The same count for the one stream that is running, for its own ledger row. */
  currentStreamSpokenSeconds() {
    return Math.round((this.streamSpokenSamples / SAMPLE_RATE_HZ) * 10) / 10;
  }

  emit(type, payload) {
    if (this.closed && type != "closed") return;
    this.onEvent(type, payload);
  }

  /**
   * Open the microphone graph and NOTHING else.
   *
   * This is the whole of the pre-open: `getUserMedia`, the 24 kHz context and
   * the worklet, with no relay, no ledger row and no billing. It exists because
   * the device handover is the slow half of starting — macOS handed Chrome an
   * iPhone's Continuity microphone across the room and the first press waited
   * ten seconds on it (2026-09-08) — and that wait has no business sitting
   * between a person pressing a button and a session that can hear them.
   *
   * Idempotent in both directions. An already-open graph re-announces itself
   * rather than opening a second one, so a caller handed this session paints
   * its caption from the same event the first caller did; an open still in
   * flight hands back the SAME promise, so a press landing mid-open waits for
   * the request already running instead of starting a second one at the device.
   *
   * @returns {Promise<void>} settled when the graph is capturing.
   */
  prepareMicrophone() {
    if (this.micReady) {
      this.emit("mic-open", this.micInfo);
      return Promise.resolve();
    }
    if (this.micOpening) return this.micOpening;
    this.micOpening = this.openMicrophone().finally(() => {
      this.micOpening = null;
    });
    return this.micOpening;
  }

  /**
   * Opens the microphone and resolves the mint AT THE SAME TIME, then the relay.
   *
   * The mint is now trivial — there is no vendor token to fetch — but the
   * signature and the overlap are kept: the caller hands in a promise, and a
   * rejection from it still takes precedence over a microphone failure so the
   * user gets the store's one actionable notification rather than two stacked.
   */
  async start(mint) {
    // From HERE the audio is worth keeping. Set before the microphone half runs
    // so that words spoken into a permission prompt, or into the relay
    // handshake, still reach the pre-buffer — while everything captured before
    // the press was dropped. See `sendAudio`.
    this.relayRequested = true;
    // allSettled, not all: a mint rejection must not leave a granted microphone
    // capturing with nothing owning it, and a denied microphone must not leave
    // the mint's rejection unhandled. Both are inspected below.
    //
    // `prepareMicrophone`, not `openMicrophone`: when the dock has already
    // pre-opened the graph this resolves at once and the press only has a relay
    // left to start.
    const [mic, minted] = await Promise.allSettled([
      this.prepareMicrophone(),
      Promise.resolve(mint),
    ]);

    if (minted.status == "rejected") {
      // The store notified already. Release whatever the microphone half got.
      this.stop("mint-failed");
      throw Object.assign(new Error("mint"), { voiceClass: "mint" });
    }
    if (mic.status == "rejected") {
      throw mic.reason;
    }
    this.credentials = minted.value || this.credentials;

    // Checked again between the two awaits: a close during the relay handshake
    // has the same shape as a close during the permission prompt.
    if (this.closed) {
      this.releaseMicrophone();
      throw Object.assign(new Error("aborted"), { voiceClass: "aborted" });
    }
    // The third named wait, and now the only one a pre-opened dock still shows.
    // It moved out of `openMicrophone` with the pre-open: a microphone opened
    // when the panel appeared is not connecting to anything, and saying so
    // would put a startup word on an idle panel.
    this.emit("connecting");
    // A close during the handshake rejects this rather than resolving onto a
    // dead session: `stop()` settles a start that is still in flight, precisely
    // so the microphone cannot be left running behind a promise nobody keeps.
    await this.openRelay();
    if (this.closed) {
      throw Object.assign(new Error("aborted"), { voiceClass: "aborted" });
    }
    // Both one-shot caps are armed here and only here: `no_speech` is disarmed
    // permanently by the first transcript, and `max_session` is an ABSOLUTE
    // deadline that nothing re-arms — which is what stops a 60 s silence timer
    // re-arming its way past the server's own 90 s ceiling.
    //
    // Armed directly rather than through `armSilenceTimer()`: that helper
    // reschedules, and a reschedule between two arms emits a `deadline` for the
    // 60 s silence timer that the 20 s cap supersedes on the very next line.
    // One `reschedule()` after all three is one `deadline` event.
    const now = Date.now();
    // The stream's own deadline, armed with the session's. It does NOT end the
    // session when it fires — `onDeadline` closes the paid half and leaves the
    // microphone, the card and the confirm window exactly where they are.
    this.timers.arm("stream_idle", this.streamIdleMs, now);
    this.timers.arm("silence", SILENCE_TIMEOUT_MS, now);
    // Not on a session that already has a card: Resume voice re-arms the kept
    // card inside the `listening` emit that `voice:ready` produces, which runs
    // BEFORE this continuation, so `prepareConfirmation` has already retired
    // the cap by the time it would be armed here. Arming it anyway put a 20 s
    // never-spoke deadline under a card whose read-back plus 12 s window can
    // outlast it (measured 2026-09-03, `probe-resume.mjs`).
    if (!this.confirmIntent) this.timers.arm("no_speech", NO_SPEECH_TIMEOUT_MS, now);
    this.timers.arm("max_session", MAX_SESSION_MS, now);
    this.reschedule();
  }

  /**
   * Close the BILLED half and keep everything else.
   *
   * This is the packet. `stop()` ends a session: it releases the microphone,
   * closes the audio context, clears every timer and reports a usage row. This
   * ends a STREAM: the relay is told to stop, the listeners come off, the
   * stream's own deadlines are disarmed — and the microphone graph, the audio
   * context, the meter, the pending card, its preview hash, its idempotency key
   * and the confirm/answer windows that belong to the CARD are all untouched.
   *
   * The Muse ingress rule is honoured in both directions here. While a stream is
   * OPEN a frame goes out every quantum, zeros included; to a CLOSED stream
   * nothing goes out at all, which `relayRequested = false` makes true for
   * `sendAudio` on the very next quantum — before any `await`, so there is no
   * window in which a frame could be emitted onto a socket the server has
   * already torn down.
   *
   * Order matters once: the server is told, and THEN the listeners are detached.
   * The `voice:stopped` it answers with would otherwise reach
   * `performRelayAction`, whose `stop` action ends the whole session — which is
   * the one thing this method exists not to do.
   *
   * @param {string} [reason] why, for the event and the server's own log
   * @returns {boolean} false when there was no stream to close
   */
  closeStream(reason = "idle") {
    if (this.closed || !this.relayRequested) return false;

    const seconds = this.currentStreamSeconds();
    const spokenSeconds = this.currentStreamSpokenSeconds();

    try {
      if (this.relayLive && socket.connected) {
        // The payload is new and the server ignores it today (its `voice:stop`
        // handler takes no body). FIX-B9 stores `timing` on `voicesessions`;
        // `spokenSeconds` rides in the same message so the row can carry
        // "billed 41 s, spoken 6 s" the moment that lands. Sending it early
        // costs nothing and means no second change here.
        socket.emit("voice:stop", { reason, spokenSeconds, billedSeconds: seconds });
      }
    } catch {
      /* the socket is going away; the server's own budgets close the relay */
    }
    this.detachRelayListeners();

    this.relayLive = false;
    this.relayReady = false;
    this.relayRequested = false;
    this.preBuffer = [];
    this.audioChunks = [];
    this.audioSamples = 0;

    // The stream's deadlines, and only those. `confirm` and `answer` belong to
    // the CARD and keep counting across the teardown — the confirm window the
    // user is watching drain is about their command, not about the socket.
    this.timers.disarm("stream_idle");
    this.timers.disarm("no_speech");
    this.timers.disarm("silence");
    this.timers.disarm("max_session");
    this.reschedule();

    // Roll this stream's numbers up and clear the per-stream ones, so the next
    // stream starts from zero and the footer keeps the total.
    this.billedSecondsBefore += seconds;
    this.lastStreamSeconds = seconds;
    this.spokenSamplesBefore += this.streamSpokenSamples;
    this.streamSpokenSamples = 0;
    this.speechMeter.reset();
    this.startedAt = null;
    this.reportedSeconds = null;

    // The start machinery, wound back so `reopenStream` can run it again.
    clearTimeout(this.readyTimer);
    this.readyTimer = null;
    this.readyResolve = null;
    this.readyReject = null;
    this.startSettled = false;
    // Per STREAM, not per session: the next stream's 20 s never-spoke cap must
    // not be retired by a transcript the previous one heard.
    this.sawTranscript = false;
    this.heardSpeech = false;
    this.quietSince = null;
    // A turn that never came back belongs to the stream that carried it. Left
    // set, it would follow the next stream around as a hold it cannot clear.
    this.awaitingResponse = false;
    this.awaitingSince = null;

    this.emit("stream-closed", {
      reason,
      seconds,
      spokenSeconds,
      billedSeconds: this.billedSecondsBefore,
    });
    return true;
  }

  /**
   * Open a new relay stream on the microphone graph that is already running.
   *
   * The device handover was paid when the panel appeared and the graph has been
   * capturing ever since, so this is only the relay: a mint, a `voice:start` and
   * the upstream handshake — measured at 0.6–1.6 s from the tap to `listening`.
   * The dock shows "Connecting" for it rather than "Listening", because a word
   * claiming a live stream over a handshake is the same lie the startup words
   * were split up to stop telling.
   *
   * `start()` does the work: it already waits on the microphone (which resolves
   * at once here), emits `connecting`, opens the relay and arms the stream's
   * deadlines. All this adds is the guard against opening a second stream on top
   * of one that is already up or already on its way.
   *
   * @param {Promise<object>|object} mint the recognition context, as `start`
   * @returns {Promise<void>}
   */
  reopenStream(mint) {
    if (this.closed) {
      return Promise.reject(
        Object.assign(new Error("aborted"), { voiceClass: "aborted" })
      );
    }
    // Already open, or opening. A second `voice:start` on one socket is refused
    // by the server with VOICE_ALREADY_STREAMING, and asking for it would turn
    // an impatient second tap into an error card.
    if (this.relayRequested) return Promise.resolve();
    return this.start(mint);
  }

  /** The dock's "Spoken replies" preference, which decides what a card is owed. */
  setRepliesOn(on) {
    this.repliesOn = on !== false;
  }

  /** True while the billed half is up. The mic graph's state is `micReady`. */
  isStreaming() {
    return !!this.relayRequested && !!this.relayReady && !!this.relayLive;
  }

  /**
   * Whether an idle stream may be torn down right now.
   *
   * The policy is pure and lives in `voiceTimers.streamIdleAllowed`; this is
   * the session reading its own state into it. `playbackHeld` covers BOTH
   * read-back voices — the relay's, played on this context, and the browser's,
   * which the dock holds the microphone for through `holdMicForPlayback` — so a
   * sentence being spoken aloud holds the stream open whichever voice is saying
   * it.
   */
  /**
   * A turn that has gone up and not come back, within the grace that bounds it.
   *
   * Two waits count: the relay's parse of a completed transcript
   * (`awaitingResponse`, set by the mapper on `voice:final` and cleared on
   * `voice:command` or `voice:error`) and the dock's own preview round trip
   * (`muted`, set for exactly that). Either means the user has spoken and is
   * waiting, and the server would cancel the parse if this stream went now.
   */
  turnInFlight() {
    const waits = [];
    if (this.awaitingResponse && this.awaitingSince) waits.push(this.awaitingSince);
    if (this.muted && this.mutedSince) waits.push(this.mutedSince);
    if (!waits.length) return false;
    return Date.now() - Math.min(...waits) < PARSE_GRACE_MS;
  }

  mayCloseStream() {
    return streamIdleAllowed({
      turnInFlight: this.turnInFlight(),
      playing: !!this.speaking || this.playbackHeld,
      confirmPending: !!this.confirmIntent,
      repliesOn: this.repliesOn,
      answerArmed: Object.prototype.hasOwnProperty.call(
        this.timers.snapshot(),
        "answer"
      ),
    });
  }

  /**
   * Speech happened, so the stream has a live reason to stay open.
   *
   * Re-arming MOVES the deadline rather than adding a second one, so this is
   * safe to call as often as the relay sends partials. It is deliberately the
   * only thing that pushes the stream's idle deadline out: not the meter, not a
   * render, not a timer — words.
   */
  noteSpeechActivity() {
    if (this.closed || !this.relayRequested) return;
    this.timers.arm("stream_idle", this.streamIdleMs, Date.now());
    this.reschedule();
  }

  /**
   * Aim the session's single timeout at whichever deadline is nearest, and tell
   * the caller which one that is.
   *
   * Called after every arm and disarm. The `deadline` event is deduplicated on
   * the `(kind, deadlineAt)` pair so a re-arm to the same instant, or a
   * disarm that does not change the answer, costs the UI nothing.
   */
  reschedule() {
    clearTimeout(this.deadlineTimer);
    this.deadlineTimer = null;
    if (this.closed) return;

    const now = Date.now();
    // Two answers from one set. `soonest` is when to wake up, over every kind.
    // `shown` is what to put on the state line, and it leaves the stream's idle
    // deadline out: that one ends nothing, says nothing, and is pushed out again
    // by every word — so a ring drawn on it would reset itself under a card
    // whose own 12 s window is the countdown the person is actually racing
    // (measured 2026-09-17, `probe-stream-life.mjs`).
    const soonest = this.timers.next(now);
    const shown = this.timers.next(now, { exclude: STREAM_TIMER_KINDS });
    const key = shown ? `${shown.kind}:${shown.deadlineAt}` : null;
    if (key !== this.lastDeadlineKey) {
      this.lastDeadlineKey = key;
      this.emit(
        "deadline",
        shown
          ? {
              kind: shown.kind,
              deadlineAt: shown.deadlineAt,
              totalMs: shown.totalMs,
            }
          : null
      );
    }
    if (!soonest) return;

    this.deadlineTimer = setTimeout(
      () => this.onDeadline(),
      delayUntilNext(this.timers, now)
    );
  }

  /**
   * The one timeout fired. Ask the set what actually ran out rather than
   * trusting the handle: a backgrounded tab, a long synchronous block or a
   * clock adjustment can wake this early, and rescheduling is the right answer
   * to all three.
   */
  onDeadline() {
    this.deadlineTimer = null;
    if (this.closed) return;

    const now = Date.now();
    // Read the window's own length BEFORE anything disarms it: the exit copy
    // must quote the window that actually ran, not whatever the current
    // "Spoken replies" preference would arm today (a toggle mid-countdown
    // otherwise reports "20 s passed" for a 12 s window — parallel review,
    // Sonnet, 2026-09-03).
    const soonest = this.timers.next(now);
    const kind = this.timers.expired(now);
    if (!kind) {
      // Nothing has run out yet. Aim again at whatever is now nearest.
      this.reschedule();
      return;
    }
    if (kind === "stream_idle") {
      // The one deadline that does not end the session. Something still owed to
      // the person on screen — a sentence being read back, a card that has been
      // promised a spoken confirm, a question waiting on an answer — buys
      // another idle period rather than a reprieve for ever, so the stream
      // closes `streamIdleMs` after the last of them clears.
      if (!this.mayCloseStream()) {
        this.timers.arm("stream_idle", this.streamIdleMs, now);
        this.reschedule();
        return;
      }
      this.closeStream("idle");
      return;
    }
    if (kind === "no_speech" && this.sawTranscript) {
      // Speech was heard, so the never-spoke cap has no claim on this session
      // even if the disarm never arrived. Kept from the old handle check.
      this.timers.disarm("no_speech");
      this.reschedule();
      return;
    }
    this.stop(
      STOP_REASON_FOR_KIND[kind],
      kind,
      soonest && soonest.kind === kind ? soonest.totalMs : null
    );
  }

  async openMicrophone() {
    // The graph is already running — the dock pre-opened it when the panel
    // appeared. Asking the device again would be a second handover, which is
    // the exact wait this path exists to have already paid.
    if (this.micReady) return;
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw Object.assign(new Error("unsupported"), { voiceClass: "unsupported" });
    }

    // Startup is three waits the user cannot see, so each one says which it is.
    // "Starting" covering all of them is what made a slow permission prompt
    // look like a hung modal.
    this.emit("requesting-mic");

    // Wall time across the handover, reported to the screen. Ten seconds of it
    // was invisible until the device menu in Chrome's address bar was opened,
    // which is not a place a person in gloves is going to look.
    const askedAt = Date.now();
    // The ledger's `captureRequested`, stamped on the MONOTONIC clock rather
    // than taken from `openMs` below. `openMs` is wall time and stays wall time
    // — it is a sentence on the screen ("opened in 1.4 s") and nothing
    // aggregates it. Everything that reaches the turn log is a difference of
    // two `performance.now()` readings, and mixing the two clocks in one number
    // is the failure `voiceTiming.js` exists to prevent.
    this.timing?.mark("captureRequested");
    try {
      this.micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch {
      // Denied, dismissed, or no device. All three mean the same thing to the
      // user and get the same calm sentence; none of them is a broken system.
      throw Object.assign(new Error("microphone"), { voiceClass: "micDenied" });
    }
    const openMs = Date.now() - askedAt;

    // The permission prompt can sit on screen indefinitely, and the user may
    // close the modal while it is up. `stop()` will already have run by then,
    // found micStream still null, and set `closed` — so it had nothing to release
    // AND it can never release anything again, because stop() returns early once
    // closed. The stream we just received would stay live with nothing owning it.
    //
    // Release it here, at the only moment it is reachable.
    if (this.closed) {
      this.releaseMicrophone();
      throw Object.assign(new Error("aborted"), { voiceClass: "aborted" });
    }

    // WHICH microphone, and how long it took to hand over. Both are facts only
    // this line can see, and the panel says both: a Continuity microphone in
    // another room is indistinguishable from a broken one until it is named.
    const track = this.micStream.getAudioTracks()[0];
    const label = track ? track.label : "";
    const settings = track ? track.getSettings() : null;
    this.micInfo = { label, settings, openMs };
    this.onEvent("mic-info", this.micInfo);
    console.info("[voice] mic", label, settings, openMs);

    this.audioContext = new AudioContext({ sampleRate: 24000 });
    const moduleUrl = URL.createObjectURL(
      new Blob([WORKLET_SOURCE], { type: "application/javascript" })
    );
    try {
      await this.audioContext.audioWorklet.addModule(moduleUrl);
    } finally {
      URL.revokeObjectURL(moduleUrl);
    }

    // The worklet load is another await the modal can be closed during. Check
    // again rather than connecting a live microphone to a dead session.
    if (this.closed) {
      this.releaseMicrophone();
      throw Object.assign(new Error("aborted"), { voiceClass: "aborted" });
    }

    // Start capturing NOW, before the relay exists — before there is even a
    // decision to start one. Frames captured before a start are dropped and
    // frames captured after it are held, which is what stops the first words of
    // a command being lost without carrying an idle room into the next session.
    this.startStreamingAudio();
    this.micReady = true;

    // The microphone is live from this line, whatever the relay is doing — or
    // whether there is one at all. The meter can run and the caption can name
    // the device, because the audio really is being captured.
    this.emit("mic-open", this.micInfo);
  }

  /**
   * Bring up the relay on the app's EXISTING socket.
   *
   * Deliberately not a second connection: the server authenticates the `jwt`
   * cookie once in its handshake and derives lab scope from it, and a second
   * socket would be a second authenticated session to keep in step with this
   * one for no gain. It is also why `stop()` never disconnects — other features
   * are using this socket.
   */
  async openRelay() {
    await this.connectSocket();
    // The connect can be a real wait, and the modal can be closed inside it.
    // Starting a relay here would leave one running with nothing owning it.
    if (this.closed) {
      throw Object.assign(new Error("aborted"), { voiceClass: "aborted" });
    }

    const ready = new Promise((resolve, reject) => {
      this.readyResolve = resolve;
      this.readyReject = reject;
    });

    this.attachRelayListeners();
    this.readyTimer = setTimeout(() => {
      this.abortStart(
        "VOICE_SOCKET_TIMEOUT",
        "The voice service did not start in time."
      );
    }, RELAY_READY_TIMEOUT_MS);

    this.relayLive = true;
    // The other end of `captureRequested`, for the case the pre-open made
    // ordinary: the microphone graph has been running since the panel appeared,
    // so the wait this turn is actually paying is the relay handshake and
    // nothing else. First-write-wins in the ledger means a session that opened
    // the device on this same turn keeps the earlier stamp and measures the
    // whole wait, device handover included — one meaning, two entry points.
    this.timing?.mark("captureRequested");
    socket.emit("voice:start", {
      mode: this.mode,
      keywords: this.relayKeywords(),
      screenPuck: this.relayScreenPuck(),
    });

    return ready;
  }

  /** Recognition hints, as the mint result or the constructor supplied them. */
  relayKeywords() {
    if (this.keywordsOption) return this.keywordsOption;
    const creds = this.credentials || {};
    if (Array.isArray(creds.keywords)) return creds.keywords;
    if (Array.isArray(creds.keyterms)) return creds.keyterms;
    return [];
  }

  /** The puck the user is looking at, which the parser resolves against. */
  relayScreenPuck() {
    if (this.screenPuckOption) return this.screenPuckOption;
    const creds = this.credentials || {};
    return creds.screenPuck || null;
  }

  connectSocket() {
    if (socket.connected) return Promise.resolve();

    return new Promise((resolve, reject) => {
      let settled = false;
      let timer = null;
      const done = (fn) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        socket.off("connect", onConnect);
        fn();
      };
      const onConnect = () => done(resolve);
      timer = setTimeout(() => {
        done(() => {
          // Nothing has been rendered about this yet, so say it on the surface
          // the user is already looking at before closing the session.
          this.emit(
            "model-error",
            relayError(
              "VOICE_SOCKET_TIMEOUT",
              "Could not reach the voice service."
            )
          );
          this.stop("start-failed");
          reject(
            Object.assign(new Error("relay-connect"), { voiceClass: "aborted" })
          );
        });
      }, SOCKET_CONNECT_TIMEOUT_MS);

      socket.on("connect", onConnect);
      // The HttpOnly JWT cookie is the credential; the browser only includes it
      // on a cross-origin handshake when this is set. App.vue sets the same flag
      // on login — this covers the modal opening before that ran.
      socket.io.opts.withCredentials = true;
      socket.connect();
    });
  }

  attachRelayListeners() {
    if (this.relayHandlers) return;

    const relay = (type) => (payload) => {
      if (this.closed) return;
      this.handleRelayEvent(type, payload);
    };

    this.relayHandlers = {
      "voice:ready": relay("voice:ready"),
      "voice:partial": relay("voice:partial"),
      "voice:final": relay("voice:final"),
      "voice:command": relay("voice:command"),
      "voice:stopped": relay("voice:stopped"),
      "voice:speak": relay("voice:speak"),
      "voice:error": (payload) => {
        if (this.closed) return;
        // An error before `voice:ready` is a start that failed — an allowance
        // that is used up, a lab without the beta, a service that is not
        // configured. Rendering the server's own sentence and stopping is more
        // useful than a generic transport failure, and it is why the reject
        // below is classed `aborted`: the modal must not stack a second card
        // saying something vaguer than the one already on screen.
        if (!this.startSettled) {
          this.abortStart(payload && payload.code, payload && payload.message);
          return;
        }
        this.handleRelayEvent("voice:error", payload);
      },
      disconnect: () => {
        this.relayLive = false;
        if (this.closed) return;
        if (!this.startSettled) {
          this.abortStart(
            "VOICE_SOCKET_TIMEOUT",
            "The connection to the voice service was lost."
          );
          return;
        }
        // An unrequested drop mid-session is `danger`: the user's words went
        // nowhere and they need to know.
        this.stop("transport-lost");
      },
    };

    Object.keys(this.relayHandlers).forEach((event) => {
      socket.on(event, this.relayHandlers[event]);
    });
  }

  detachRelayListeners() {
    if (!this.relayHandlers) return;
    Object.keys(this.relayHandlers).forEach((event) => {
      socket.off(event, this.relayHandlers[event]);
    });
    this.relayHandlers = null;
  }

  /**
   * The whole server-to-caller mapping, in three ordered steps.
   *
   * State first so an action or an emit cannot read a stale flag; actions
   * second because they own the timers a proposal's emit depends on; emits
   * last because the modal can synchronously stop the session inside one (a
   * spoken "confirm" applies and calls `stop`), and anything sequenced after
   * that would be dropped by `emit`'s closed guard.
   */
  handleRelayEvent(type, payload) {
    const result = mapRelayEvent(type, payload, this);
    const wasAwaiting = this.awaitingResponse;
    Object.assign(this, result.patch);
    // Stamp the TRANSITION, here rather than in the mapper: the mapper is pure
    // and reads no clock, and this is the only place its patch is applied. The
    // stamp is what lets `turnInFlight` bound the hold a pending parse puts on
    // the stream.
    if (this.awaitingResponse && !wasAwaiting) this.awaitingSince = Date.now();
    if (!this.awaitingResponse) this.awaitingSince = null;
    result.actions.forEach((action) => this.performRelayAction(action));
    result.emits.forEach((entry) => this.emit(entry.type, entry.payload));
  }

  performRelayAction(action) {
    switch (action.kind) {
      case "ready":
        this.handleRelayReady(action.voiceSessionId);
        break;
      case "clearAnswerWindow":
        this.clearAnswerWindow();
        break;
      case "clearNoSpeechCap":
        this.clearNoSpeechCap();
        break;
      case "armSilenceTimer":
        this.armSilenceTimer();
        break;
      case "armAnswerWindow":
        this.armAnswerWindow();
        break;
      case "raiseProposal":
        // The action itself is the fourth argument: it carries the parse's own
        // measurements (`latencyMs`, `usd`) alongside the call, and passing it
        // whole means a measurement the mapper starts sending needs no change
        // here to reach the dock.
        this.raiseProposal(action.callId, action.name, action.argsJson, action);
        break;
      case "speak":
        this.handleSpeak(action.payload);
        break;
      case "stop":
        // The server has already torn its side down; do not answer with a
        // `voice:stop` it would have to ignore.
        this.relayLive = false;
        this.reportedSeconds = action.seconds;
        this.stop(action.reason);
        break;
      default:
        break;
    }
  }

  handleRelayReady(voiceSessionId) {
    clearTimeout(this.readyTimer);
    this.readyTimer = null;
    // `VoiceCommandModal` reads exactly one thing off this object, the id it
    // reports usage against, so the rest of the mint result is preserved.
    this.credentials = { ...(this.credentials || {}), voiceSessionId };
    // The honest start of the elapsed-time and cost display: this is the first
    // instant audio is forwarded to something that charges for it. It used to
    // start when the graph opened, which was the same moment until the panel
    // began pre-opening the microphone ahead of the press.
    //
    // Per STREAM now, not per session. `closeStream` nulls it and rolls that
    // stream's seconds into `billedSecondsBefore`, so this clock always measures
    // the one thing that is billing and `elapsedSeconds()` always reports the
    // sum.
    if (!this.startedAt) {
      this.startedAt = Date.now();
      this.emit("capture-started", { startedAt: this.startedAt });
    }
    // Capture is LIVE: this is the first instant a frame the microphone took
    // reaches anything at all, which is what "capture" means to the person
    // waiting to be heard. Deliberately not `mic-open` — a graph capturing into
    // a dropped-frame void is not a microphone anybody can talk into — so the
    // span covers the device handover AND the handshake when both were paid,
    // and the handshake alone when the graph was already warm.
    this.timing?.mark("captureLive");
    // The billed half is up. Said as its own event because "listening" is a
    // word about the user and this is a fact about the wire: the dock's resting
    // word, its clock and its card copy all turn on it.
    this.emit("stream-open", {
      voiceSessionId,
      startedAt: this.startedAt,
      billedSeconds: this.billedSecondsBefore,
    });
    this.flushPreBuffer();

    if (this.startSettled) return;
    this.startSettled = true;
    const resolve = this.readyResolve;
    this.readyResolve = null;
    this.readyReject = null;
    if (resolve) resolve();
  }

  /**
   * A start that will not happen. Says why on the surface the user is looking
   * at, ends the session, and rejects as `aborted` so the modal reports nothing
   * further — the message is already on screen and vaguer copy over the top of
   * it is worse than silence.
   */
  abortStart(code, message) {
    if (this.startSettled) return;
    this.startSettled = true;
    clearTimeout(this.readyTimer);
    this.readyTimer = null;

    this.sawError = true;
    this.emit("model-error", relayError(code, message));

    const reject = this.readyReject;
    this.readyResolve = null;
    this.readyReject = null;
    this.stop("start-failed");
    if (reject) {
      reject(Object.assign(new Error("relay-start"), { voiceClass: "aborted" }));
    }
  }

  startStreamingAudio() {
    this.sourceNode = this.audioContext.createMediaStreamSource(this.micStream);
    this.workletNode = new AudioWorkletNode(this.audioContext, "sa-pcm");

    this.workletNode.port.onmessage = ({ data }) => {
      // EVERY quantum produces a frame while the graph is open. Nothing here
      // returns early on a reason to be silent, because the reasons to be
      // silent are reasons to send SILENCE, not reasons to send nothing.
      //
      // Measured 2026-09-03 against the real service: Muse enforces real-time
      // ingress, so a gap of even one second is answered with "Ingress audio
      // slower than real-time" and the stream is closed (code 1008) about
      // 11.5 s later whether or not frames resume. The two early returns that
      // used to live here — `muted` for preview/apply HTTP and `playbackHeld`
      // for a read-back — therefore ended the session roughly 11.5 s into every
      // proposal that was spoken aloud.
      if (this.closed) return;

      const gateOpen = this.gate.isOpen();
      // The meter is driven from here and only from here, so it reflects real
      // captured audio rather than elapsed time (DESIGN.md section 10). While
      // muted, or while the hold-to-talk gate is shut, it reads zero — which is
      // the truth: what leaves the machine in both cases is silence, and a
      // meter moving for it would advertise a recording that is not happening.
      this.emit("level", this.muted || !gateOpen ? 0 : data.rms);

      const captured = new Int16Array(data.buffer);
      const frame = outboundFrame(captured, {
        gateOpen,
        muted: this.muted,
        playbackHeld: this.playbackHeld,
      });
      // The speaking indicator is about the room, so it is fed only when the
      // frame carries the room — which `outboundFrame` says by handing the
      // captured array straight back. Feeding it zeros would let the latch drop
      // mid-hold, and feeding it the microphone while a read-back plays would
      // raise "speaking" for the machine's own voice.
      if (frame === captured) {
        // TWO tests, not one. `frame === captured` says the audio reached the
        // wire; the meter says somebody was talking in it. Only the second one
        // is new: FIX-F11 counted every forwarded quantum, so a held button
        // over a quiet room reported itself as pure speech and the ratio the
        // admin page draws could never exceed 1.00×.
        //
        // The meter runs on every forwarded quantum, so its hangover is
        // continuous across the moment a stream is asked for; what
        // `relayRequested` gates is only whether the samples are ADDED, because
        // audio that reaches no wire is not part of what was billed and
        // `spokenSeconds` is read against `billedSeconds`.
        const spoken = this.speechMeter.observe(data.rms, captured.length);
        if (this.relayRequested) this.streamSpokenSamples += spoken;
        this.observeLevel(data.rms);
      } else {
        // The run of captured audio just broke — shut gate, muted apply, or a
        // read-back holding the microphone. A hangover left open across it
        // would credit the first quiet quantum of the next hold to the last
        // word of the previous one.
        this.speechMeter.reset();
      }
      this.appendAudio(frame);
    };

    this.sourceNode.connect(this.workletNode);
    // NO clock is started here. The graph can be open for minutes before anyone
    // presses anything — that is the pre-open — and a cost display started by
    // capture would bill an idle panel for a service it has not contacted.
    // `handleRelayReady` owns `startedAt`, because forwarding is the thing that
    // costs money. Deliberately NOT connected to destination either: routing the
    // microphone to the speakers would put the user's own voice in their ear.
  }

  /** Accumulate render quanta until there is at least one 80 ms frame to send. */
  appendAudio(samples) {
    // Hold-to-talk. A shut gate hands back silence of the same length, so the
    // quanta still coalesce, still go through `sendAudio`, and still keep both
    // the relay's no-audio timer satisfied and Muse's endpointer advancing —
    // which is the whole reason the gate lives here instead of pausing the
    // stream. Nothing said off-button reaches the wire.
    samples = this.gate.filter(samples);
    if (!samples.length) return;
    this.audioChunks.push(samples);
    this.audioSamples += samples.length;
    if (this.audioSamples < AUDIO_FRAME_SAMPLES) return;

    const frame = new Int16Array(this.audioSamples);
    let offset = 0;
    this.audioChunks.forEach((chunk) => {
      frame.set(chunk, offset);
      offset += chunk.length;
    });
    this.audioChunks = [];
    this.audioSamples = 0;
    this.sendAudio(frame.buffer);
  }

  /**
   * Raw PCM16 as a binary socket.io message. No base64: socket.io carries an
   * ArrayBuffer natively, and a third of the bytes on a continuous stream is
   * not a tax worth paying for an encoding nothing needs.
   */
  sendAudio(buffer) {
    // NO STREAM. Two ways to be here now: the microphone was pre-opened and
    // nothing has been pressed yet, or a stream was torn down by the idle timer
    // and the panel is resting. Both drop the frame outright — the Muse ingress
    // rule is about what an OPEN stream is owed, and a closed one is owed
    // nothing at all. Dropping rather than holding is also what stops an idle
    // panel from opening its next stream with three seconds of whatever the room
    // was doing beforehand.
    if (!this.relayRequested) return;
    if (!this.relayReady || !this.relayLive || !socket.connected) {
      // Hold it. Oldest frames go first if the relay is slow enough to overrun
      // the window — losing the start of a long wait is better than growing
      // without bound, and better than flushing a queue the server would drop.
      //
      // This is the ONE place a frame waits, and it waits for a stream that has
      // been ASKED FOR and has not answered yet — the 0.6–1.6 s between the tap
      // and `voice:ready` on a reopen, the same gap the first start has always
      // had. Nothing is written to the socket from here; `flushPreBuffer` runs
      // on `voice:ready` and not before, so the invariant "nothing goes out to a
      // closed stream" holds whether the frame was dropped above or held here.
      // The packet asked for these frames to be dropped instead, on the grounds
      // that the ingress rule forbids buffering. Measured, the rule is the other
      // way round: Muse refuses audio that arrives SLOWER than real time (a gap
      // of a second is answered with "Ingress audio slower than real-time" and a
      // 1008 close ~11.5 s later, probe-muse-gap.mjs), and a flush at ready
      // arrives FASTER than real time, bounded at ~3 s by PREBUFFER_MAX_FRAMES
      // and well inside the server's own 5 s backlog guard. Dropping would lose
      // the first word of every reopened turn to buy nothing.
      this.preBuffer.push(buffer);
      if (this.preBuffer.length > PREBUFFER_MAX_FRAMES) this.preBuffer.shift();
      return;
    }
    socket.emit("voice:audio", buffer);
  }

  /** Send everything captured while the relay was still starting. */
  flushPreBuffer() {
    if (!this.preBuffer.length) return;
    const held = this.preBuffer;
    this.preBuffer = [];
    held.forEach((buffer) => socket.emit("voice:audio", buffer));
  }

  /**
   * Local voice activity, for the speaking indicator only.
   *
   * It no longer ends anything. The relay endpoints in hands-free mode and
   * emits a `voice:final` per spoken turn on one stream, so a client-side
   * trailing-silence stop would end the conversation at its first pause.
   */
  observeLevel(rms) {
    // A pending proposal owns a one-shot decision window; noise must not move
    // anything inside it.
    if (this.confirmIntent) {
      this.quietSince = null;
      return;
    }

    if (rms >= ENDPOINT_RMS_THRESHOLD) {
      this.quietSince = null;
      if (!this.heardSpeech) {
        this.heardSpeech = true;
        // The RISING EDGE only, once per utterance. Someone who is audibly
        // talking has a live claim on the stream even before the relay's first
        // partial comes back, and the alternative — re-arming per quantum —
        // would be 190 `clearTimeout`/`setTimeout` pairs a second.
        this.noteSpeechActivity();
        this.emit("listening", { speaking: true });
      }
      return;
    }

    if (!this.heardSpeech) return;
    const now = Date.now();
    if (this.quietSince == null) {
      this.quietSince = now;
      return;
    }
    if (now - this.quietSince >= ENDPOINT_SILENCE_MS) {
      // Released, so the next utterance raises the indicator again.
      this.heardSpeech = false;
      this.quietSince = null;
    }
  }

  /** Re-armed on every transcript: a session that is being talked to is alive. */
  armSilenceTimer() {
    const now = Date.now();
    this.timers.arm("silence", SILENCE_TIMEOUT_MS, now);
    // A completed turn is speech activity, so the stream's own idle deadline
    // moves with the session's.
    if (this.relayRequested) this.timers.arm("stream_idle", this.streamIdleMs, now);
    this.reschedule();
  }

  armAnswerWindow() {
    if (this.confirmIntent) return;
    // A rendered question retires the never-spoke cap, for the same measured
    // reason `prepareConfirmation` does: the question is proof the session is
    // doing something, and the 20 s cap is otherwise still armed on a turn that
    // reached the screen without a relay transcript.
    this.timers.disarm("no_speech");
    this.timers.arm("answer", ANSWER_WINDOW_MS, Date.now());
    this.reschedule();
  }

  clearAnswerWindow() {
    this.timers.disarm("answer");
    this.reschedule();
  }

  /**
   * One-shot, and nothing re-arms it: the first transcript retires it for good.
   *
   * It is also the only action a PARTIAL raises that reaches the timers, so the
   * stream's idle deadline is moved here too — someone mid-sentence is the case
   * the whole idle timer must never fire under, and a partial is the earliest
   * proof of it the relay can give.
   */
  clearNoSpeechCap() {
    this.timers.disarm("no_speech");
    if (this.relayRequested) {
      this.timers.arm("stream_idle", this.streamIdleMs, Date.now());
    }
    this.reschedule();
  }

  /**
   * A parsed command became a proposal. This raises an event and stops; it does
   * not call preview itself, because the caller owns the HTTP layer and the
   * store. The classification is in `voiceRelayEvents.classifyToolCall` so the
   * `preview_` strip and the clarification branch have a test.
   *
   * `meta` is the mapper's own action, and the two numbers taken off it are the
   * parse's: how long it took and what it cost. They are carried on the
   * proposal rather than on a channel of their own because they describe THIS
   * call — a session that raises three proposals has three latencies and three
   * prices, and the dock adds up the second while showing the first.
   *
   * @param {string} callId
   * @param {string|null} intentName
   * @param {string} rawArguments
   * @param {{latencyMs?: number|null, usd?: number,
   *   confirmMode?: string|null}|null} [meta]
   */
  raiseProposal(callId, intentName, rawArguments, meta = null) {
    const call = classifyToolCall(intentName, rawArguments);

    if (call.kind === "ignored") return;

    if (call.kind === "unparseable") {
      // Arguments that did not survive. Drop the turn rather than proposing
      // something nobody can read.
      this.respondToTool(callId);
      return;
    }

    if (call.kind === "clarification") {
      // A structured ask, not a command: it renders as a question on the reply
      // surface and must NEVER reach the command endpoints (the server 404s it
      // by design). The mic stays open for the answer.
      this.emit("clarification", call.fields);
      // A clarification IS "the service asked a question", but it arrives as a
      // tool call, so `turnHadCall` gates it out of the spoken-question path.
      // Arm the bounded window here or the open mic idles on the 60s silence
      // timer instead of the 15s answer window — the exact billed-silence
      // exposure that window exists to close.
      this.armAnswerWindow();
      this.respondToTool(callId);
      return;
    }

    this.outcome.commandsPreviewed += 1;
    const measured = meta || {};
    this.emit("proposal", {
      callId,
      intent: call.intent,
      fields: call.fields,
      // Null, never 0, for a latency nobody measured: the dock prints an em
      // dash for the first and "0.0 s" for the second, and only one of those
      // is true of a relay that did not tell us.
      latencyMs: Number.isFinite(measured.latencyMs) ? measured.latencyMs : null,
      usd: Number.isFinite(measured.usd) && measured.usd > 0 ? measured.usd : 0,
      // How this card may be confirmed, carried and not interpreted: the relay
      // says `tap` for a delete in a shared-terminal lab and says nothing at
      // all in every other case, including on every relay deployed today. The
      // dock normalises it once, in `confirmModeOf`, so there is one place that
      // decides what an unrecognised word means.
      confirmMode: measured.confirmMode || null,
    });
  }

  /**
   * Retires a call id.
   *
   * There is no model on the other end of this any more. The relay parses a
   * transcript and is done with it: it is not waiting for a tool result, and
   * there is no conversation to continue. The clarification wording the user
   * sees comes from the modal's own `CLARIFICATION_COPY`. Kept as a method, and
   * still resolving, because `raiseProposal`'s two dead-end branches read as
   * "answer the call and stop" and that is worth keeping legible.
   */
  respondToTool(callId) {
    if (callId) delete this.pendingCalls[callId];
    return Promise.resolve();
  }

  /**
   * Push-to-talk only: the caller has released the button, so the utterance is
   * over. Hands-free never calls this — the server endpoints, and the stream
   * stays open across turns until `stop()`.
   */
  endUtterance() {
    if (this.closed || !this.relayLive || !socket.connected) return;
    socket.emit("voice:end");
  }

  /**
   * Stops forwarding captured audio without tearing the session down. Used
   * while preview HTTP or apply HTTP is in flight. It deliberately does not
   * move either billing backstop; proposal decisions use the confirm window.
   */
  setMuted(muted) {
    const was = this.muted;
    this.muted = !!muted;
    // The same stamp `awaitingResponse` gets, for the same reason: while this is
    // set an HTTP round trip belonging to the turn on screen is running, and the
    // stream has to outlast it — bounded, so a mute that is never cleared cannot
    // keep a paid stream open for ever.
    if (this.muted && !was) this.mutedSince = Date.now();
    if (!this.muted) this.mutedSince = null;
  }

  /**
   * Hold-to-talk: open the gate while the button is down, shut it on release.
   *
   * Deliberately not `setMuted`'s twin. Muting stops frames reaching the wire
   * at all, which a stream can afford for the second an apply is in flight;
   * the gate keeps sending — silence — because Muse endpoints on AUDIO time,
   * and a stream that goes quiet never endpoints the utterance the user has
   * just finished saying (probe P1.a).
   *
   * The speaking indicator is a latch over recent RMS, and the level detector
   * is fed only while the gate is open — a closed gate is sending zeros, and an
   * indicator that kept moving for them would be describing audio nobody is
   * receiving. Clearing the latch on every transition is what stops "speaking"
   * surviving a release or arriving already raised at the next press.
   */
  setGate(open) {
    if (open) this.gate.open();
    else this.gate.close();
    this.heardSpeech = false;
    this.quietSince = null;
  }

  /* ------------------------------------------------------- read-backs -- */

  /**
   * Ask the relay to speak a sentence. Returns false synchronously when there
   * is no live relay to ask — before `voice:ready`, after a stop, or with the
   * socket gone — so the caller can fall back to the browser voice at once
   * rather than waiting for an answer that will never come.
   *
   * The audio comes back as `voice:speak` chunks and is played on THIS
   * session's AudioContext: 24 kHz PCM on the 24 kHz microphone graph, no
   * resampling, no second context to unlock on a phone. While it plays the mic
   * frames are held exactly as they are for `speechSynthesis`, so the reply
   * cannot be transcribed back as a command.
   */
  say(kind, text, turnId = null) {
    if (this.closed || !this.relayLive || !this.relayReady || !socket.connected) {
      return false;
    }
    if (!this.audioContext) return false;
    this.cancelSpeak();
    this.sayCounter += 1;
    const id = turnId === null || turnId === undefined ? `say-${this.sayCounter}` : turnId;
    this.speaking = {
      turnId: String(id),
      assembler: createChunkAssembler(id),
      sources: [],
      // The most recently scheduled source. Its `onended` is what ends the
      // read-back, so a frame that arrives late moves the finish line with it.
      lastSource: null,
      nextStartAt: 0,
      started: false,
      // `ctx.currentTime` when the first frame was scheduled, so the backstop
      // timer can tell "the audio played" from "the clock never moved".
      startedCtxTime: 0,
      done: false,
      partial: false,
      // Characters the relay reports having synthesised, off its terminator.
      // The read-back is billed on these and not on the length of the sentence
      // sent, which is why the number is taken from the answer rather than
      // computed from `text` here.
      chars: 0,
      endTimer: null,
      // Frames held while a suspended context is being resumed, in order.
      resuming: false,
      queued: [],
    };
    socket.emit("voice:say", { turnId: id, kind, text });
    return true;
  }

  /** One `voice:speak` message, routed through the assembler. */
  handleSpeak(payload) {
    const speaking = this.speaking;
    if (!speaking) return;
    const result = speaking.assembler.push(payload);
    if (result.unavailable) {
      this.finishSpeak("unavailable", result.reason);
      return;
    }
    result.frames.forEach((frame) => this.scheduleFrame(frame));
    if (result.done) {
      speaking.done = true;
      speaking.partial = result.partial;
      speaking.chars = result.chars;
      // A frame that fills a gap AFTER the terminator comes back through here
      // with `done` still set (`createChunkAssembler` latches it), so this is
      // also the re-arm for a sentence that just got longer.
      this.armSpeakEnd();
    }
  }

  /**
   * Queue one frame back to back after the last, on the session's context.
   *
   * A SUSPENDED context is handled before anything is scheduled, not alongside
   * it. Its clock is frozen, so a sentence scheduled on it plays nothing and
   * `currentTime` never reaches the end the timer was aimed at; the old code
   * fired `resume()` without awaiting it and swallowed the rejection, which is
   * how a read-back nobody heard reported `ended` and opened a confirm window
   * (review C SF4). Frames wait in order behind the resume, and a refusal — an
   * autoplay policy with no fresh gesture — becomes `unavailable`, which is the
   * signal the dock already falls back to the browser voice on.
   */
  scheduleFrame(int16) {
    const speaking = this.speaking;
    const ctx = this.audioContext;
    if (!speaking || !ctx || !int16 || !int16.length) return;

    if (ctx.state === "suspended" && typeof ctx.resume === "function") {
      speaking.queued.push(int16);
      if (!speaking.resuming) {
        speaking.resuming = true;
        let resumed;
        try {
          resumed = ctx.resume();
        } catch (err) {
          resumed = Promise.reject(err);
        }
        Promise.resolve(resumed).then(
          () => this.flushSpeakQueue(speaking),
          () => {
            if (this.speaking !== speaking) return;
            this.finishSpeak("unavailable", "suspended");
          }
        );
      }
      return;
    }
    // Anything already waiting behind a resume keeps its place in the sentence.
    if (speaking.queued.length) {
      speaking.queued.push(int16);
      return;
    }
    this.playFrame(speaking, ctx, int16);
  }

  /** Play what was held while the context resumed, in order, and re-arm the end. */
  flushSpeakQueue(speaking) {
    if (this.speaking !== speaking) return;
    speaking.resuming = false;
    const ctx = this.audioContext;
    const held = speaking.queued;
    speaking.queued = [];
    if (ctx) held.forEach((frame) => this.playFrame(speaking, ctx, frame));
    // `done` can arrive while the resume is still in flight, and `armSpeakEnd`
    // cannot measure a sentence whose frames are not scheduled yet, so it defers
    // to here.
    if (this.speaking === speaking && speaking.done) this.armSpeakEnd();
  }

  /** One buffer source, started immediately after the previous one. */
  playFrame(speaking, ctx, int16) {
    if (this.speaking !== speaking) return;
    try {
      // The buffer is declared at the relay's rate, not the context's: if a
      // phone ever hands back a 48 kHz context the browser resamples on
      // playback and the words still come out at the right pitch.
      const buffer = ctx.createBuffer(1, int16.length, SAMPLE_RATE_HZ);
      buffer.getChannelData(0).set(int16ToFloat32(int16));
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      // Playback only. The microphone source is never connected to the
      // destination; this is the reply, not the room.
      source.connect(ctx.destination);
      // `schedulePlan()` in `voiceSpeakChunks.js` computes exactly this
      // arithmetic for a whole list of frames at once. It is SPARE here on
      // purpose: frames arrive one at a time over the socket, so the running
      // cursor `speaking.nextStartAt` is the form that fits, and a plan built
      // per frame would be a list of one.
      const at = Math.max(ctx.currentTime + 0.02, speaking.nextStartAt);
      // The read-back is over when the last scheduled source REALLY finishes.
      // This is the primary end signal; the timer below it is only a backstop.
      // It also releases the node from the graph, which nothing used to do.
      source.onended = () => {
        try {
          source.disconnect();
        } catch {
          /* already disconnected */
        }
        if (this.speaking !== speaking) return;
        if (!speaking.done || source !== speaking.lastSource) return;
        this.finishSpeak("ended");
      };
      source.start(at);
      speaking.nextStartAt = at + buffer.duration;
      speaking.sources.push(source);
      speaking.lastSource = source;
      if (!speaking.started) {
        speaking.started = true;
        speaking.startedCtxTime = ctx.currentTime;
        // The first frame of this sentence is on the graph's schedule, which is
        // the earliest moment anything is going to come out of the speaker.
        // Stamped here rather than on the dock's `speak` event because the
        // scheduling is the fact and the event is a report of it.
        this.timing?.mark("ttsFirstAudible");
        this.holdMicForPlayback(true);
        this.emit("speak", { turnId: speaking.turnId, state: "started" });
      }
    } catch {
      // A context that refuses a buffer is a read-back that will not play;
      // report it as unavailable so the caller falls back to the browser voice.
      this.finishSpeak("unavailable", "playback");
    }
  }

  /** The relay has sent `done`: aim the backstop at the last frame's end. */
  armSpeakEnd() {
    const speaking = this.speaking;
    const ctx = this.audioContext;
    if (!speaking) return;
    clearTimeout(speaking.endTimer);
    speaking.endTimer = null;
    // Frames are still held behind a `resume()`, so the sentence has no length
    // yet. `flushSpeakQueue` arms this again once they are scheduled.
    if (speaking.queued.length) return;
    if (!speaking.started || !ctx) {
      // Done with nothing played: an empty sentence, or every chunk missing.
      this.finishSpeak(speaking.started ? "ended" : "unavailable", "empty");
      return;
    }
    speaking.endTimer = setTimeout(
      () => this.speakEndBackstop(speaking),
      speakEndRearmMs(ctx.currentTime, speaking.nextStartAt)
    );
  }

  /**
   * The end timer fired before any `onended` did.
   *
   * That is normal on a browser that fires `onended` late; it is also what a
   * stalled context looks like, and — because this is a wall-clock timer aimed
   * at a context-clock deadline — what a context that was SUSPENDED AND RESUMED
   * mid-sentence looks like. `speakEndDecision` separates the three by asking
   * the audio clock, and it is pure so `test-voice-speak-chunks.js` can pin the
   * boundaries without an AudioContext.
   *
   * `rearm` is the case this used to get wrong: it read "the clock advanced at
   * all" as "the audio finished", so a read-back with three seconds left to
   * play reported `ended`, released the microphone hold and let the dock open
   * the confirm window — and the rest of the machine's own voice went into the
   * microphone stream.
   */
  speakEndBackstop(speaking) {
    if (this.speaking !== speaking) return;
    const ctx = this.audioContext;
    const decision = speakEndDecision({
      state: ctx ? ctx.state : null,
      currentTime: ctx ? ctx.currentTime : null,
      startedCtxTime: speaking.startedCtxTime,
      nextStartAt: speaking.nextStartAt,
    });

    if (decision === "ended") {
      this.finishSpeak("ended");
      return;
    }

    if (decision === "rearm") {
      // Still playing. Aim the backstop at the last frame's end again, through
      // the one method that owns that timer — the remainder is measured against
      // a running context's own clock, so each pass is shorter than the last,
      // and a context that stops running answers `unavailable` next time
      // rather than looping.
      this.armSpeakEnd();
      return;
    }

    // The caller is about to say this sentence in the browser voice. Drop the
    // scheduled buffers first, or a context that resumes a moment later reads
    // the same sentence over the top of it in a second voice.
    this.stopSpeakSources(speaking);
    this.finishSpeak("unavailable", "suspended");
  }

  /** Silence every source scheduled for one read-back. Safe to call twice. */
  stopSpeakSources(speaking) {
    speaking.sources.forEach((source) => {
      try {
        source.stop();
      } catch {
        /* already finished */
      }
    });
  }

  /**
   * Close out the current read-back and release the microphone hold.
   *
   * `chars` rides out on every terminal state, `cancelled` included: a sentence
   * the relay had already synthesised and reported was billed for whether or
   * not the dock waited for the audio, and the footer's estimate is a bill
   * rather than a description of what was heard. It is 0 until the relay's own
   * terminator arrives, which is the honest number for a turn that never got
   * one — `unavailable` from an upstream that never spoke costs nothing.
   */
  finishSpeak(state, reason = null) {
    const speaking = this.speaking;
    if (!speaking) return;
    this.speaking = null;
    clearTimeout(speaking.endTimer);
    speaking.endTimer = null;
    speaking.queued = [];
    // Only a read-back that REACHED ITS END is a read-back the user heard out.
    // `cancelled` (their next sentence cut it), `unavailable` (the relay never
    // spoke) and a truncated turn are all sentences that stopped happening, and
    // stamping them would put the length of a thing nobody heard into the
    // percentile for how long people listen.
    // `partial` is the same exclusion the dock makes: the relay's chunks had a
    // gap, the user heard half a sentence, and the browser voice is about to
    // say the whole thing — so the span belongs to THAT read-back, not this one.
    if (state === "ended" && !speaking.partial) this.timing?.mark("ttsEnded");
    this.holdMicForPlayback(false);
    this.emit("speak", {
      turnId: speaking.turnId,
      state,
      reason,
      partial: !!speaking.partial,
      chars: Number(speaking.chars) || 0,
    });
  }

  /** Stop any read-back now. Safe to call with nothing playing. */
  cancelSpeak() {
    const speaking = this.speaking;
    if (!speaking) return;
    this.stopSpeakSources(speaking);
    this.finishSpeak("cancelled");
  }

  /** Hold captured frames out of both the relay and the level detector for TTS. */
  holdMicForPlayback(held) {
    this.playbackHeld = !!held;
    if (this.playbackHeld) this.quietSince = null;
  }

  /**
   * Mark a rendered proposal pending before browser speech begins.
   *
   * `spoken: false` is the shared-terminal card (`confirmMode: 'tap'`). The
   * timer hygiene is identical — a card is on screen either way, and the
   * never-spoke cap below must be retired for it either way — but NO
   * `confirmIntent` is installed, and that single omission IS the rule.
   * `classifyVoiceConfirmation` is only ever reached through a confirm intent,
   * and `startConfirmWindow` returns early without one, so a tap-only card
   * cannot grow a spoken window later by any route: not the read-back's end,
   * not the gear's replies-off path, not a resume. There is no separate flag
   * for those three callers to forget to check.
   *
   * The same omission leaves `confirmPending` false for `mayCloseStream`, which
   * is correct and is the point: a card nobody may speak to has no word to hold
   * a paid stream open for, so the stream goes idle under it and the card's own
   * line says that Confirm is still a button (`cardStatusLine`).
   *
   * @param {string|null} intent
   * @param {{spoken?: boolean}} [options] `spoken: false` for a tap-only card
   */
  prepareConfirmation(intent, { spoken = true } = {}) {
    this.timers.disarm("confirm");
    this.timers.disarm("answer");
    // A RENDERED CARD retires the never-spoke cap, which until now only a relay
    // transcript could. Measured 2026-09-03 (`probe-readback.mjs`): a card that
    // arrives by re-parse, or on a session resumed after a stop, leaves the
    // 20 s cap armed with no transcript to retire it — so a read-back longer
    // than about eight seconds ends the session before the confirm window ever
    // opens, and the state line reports "no speech" under a card the user is
    // sitting there reading.
    this.timers.disarm("no_speech");
    this.confirmIntent = (spoken ? intent : null) || null;
    this.confirmWindowStarted = false;
    this.wrongConfirmPhraseEmitted = false;
    this.confirmationTurnKey = null;
    this.quietSince = null;
    this.reschedule();
  }

  /**
   * Start the proposal's one-shot decision deadline; repeated calls are inert.
   *
   * `repliesOn` is the read-time question, not a preference lookup: with spoken
   * replies ON this is called when the read-back finishes and 12 s is a spoken
   * decision's worth of time; with them OFF it is called at render and the
   * person is reading, so `confirmWindowMs` widens it to the 20 s floor.
   * Calling with no argument keeps the historical 12 s exactly.
   *
   * @param {object} [options]
   * @param {boolean} [options.repliesOn=true]
   */
  startConfirmWindow(options = {}) {
    if (this.closed || !this.confirmIntent || this.confirmWindowStarted) return;
    this.confirmWindowStarted = true;
    this.timers.arm("confirm", confirmWindowMs(options), Date.now());
    this.reschedule();
  }

  /** Abandon a pending decision without moving either global billing backstop. */
  cancelConfirmation() {
    this.timers.disarm("confirm");
    this.confirmIntent = null;
    this.confirmWindowStarted = false;
    this.wrongConfirmPhraseEmitted = false;
    this.confirmationTurnKey = null;
    this.quietSince = null;
    this.reschedule();
  }

  noteApplied() {
    this.outcome.commandsApplied += 1;
  }

  noteRefusal() {
    this.outcome.refusals += 1;
  }

  releaseMicrophone() {
    try {
      if (this.micStream) this.micStream.getTracks().forEach((t) => t.stop());
    } catch {
      /* already released */
    }
    this.micStream = null;
    // The graph is gone with the tracks, so a later `prepareMicrophone` on this
    // session opens a new one rather than handing back a dead reuse.
    this.micReady = false;
  }

  /**
   * Idempotent. Safe to call from an error path and again from unmount.
   *
   * @param {string} reason the vocabulary the modal already understands
   *   (`idle`, `timeout`, `user`, `transport-lost`, …), unchanged.
   * @param {string|null} [kind] which timer ran out, when one did. Server-driven
   *   and user-driven stops pass nothing: `reason` alone says all there is to
   *   say about those, and inventing a kind for them would put a countdown's
   *   exit copy on a session that no countdown ended.
   */
  stop(reason, kind = null, totalMs = null) {
    if (this.closed) return;
    // A read-back mid-sentence stops with the session: its buffers are on the
    // context that is about to close, and its caller is told `cancelled`.
    this.cancelSpeak();
    this.closed = true;

    clearTimeout(this.deadlineTimer);
    this.deadlineTimer = null;
    this.timers.clear();
    this.lastDeadlineKey = null;
    clearTimeout(this.readyTimer);
    this.readyTimer = null;

    // A start still in flight is settled HERE, or it never settles at all: the
    // listeners are about to be removed, so the `voice:ready` it is waiting for
    // can no longer arrive. `aborted` is the honest class — this session was
    // torn down by its owner — and the modal already treats it as "not a
    // failure, say nothing".
    if (!this.startSettled) {
      this.startSettled = true;
      const reject = this.readyReject;
      this.readyResolve = null;
      this.readyReject = null;
      if (reject) {
        reject(Object.assign(new Error("aborted"), { voiceClass: "aborted" }));
      }
    }

    // TWO numbers, because a panel-open can now hold several streams.
    //
    // `seconds` is THIS STREAM's — the server's forwarded-audio count when it
    // sent one, wall clock otherwise — and it is what the usage report PATCHes
    // onto `voicesessions`, because the id it reports against is this stream's
    // row. The streams that closed earlier have rows of their own and the relay
    // has already finalised them; adding their seconds here would count them
    // twice on the admin page.
    //
    // `billedSeconds` is the whole panel-open, which is what the footer's clock
    // shows and the only number that lets a person see the teardown working.
    const streamRunning = this.startedAt !== null || this.reportedSeconds != null;
    const seconds = streamRunning
      ? this.currentStreamSeconds()
      : this.lastStreamSeconds;
    const billedSeconds = this.elapsedSeconds();
    const spokenSeconds = this.spokenSeconds();

    try {
      if (this.relayLive && socket.connected) {
        socket.emit("voice:stop", {
          reason,
          spokenSeconds: this.currentStreamSpokenSeconds(),
          billedSeconds: seconds,
        });
      }
    } catch {
      /* the socket is going away; the server's own budgets close the relay */
    }
    this.relayLive = false;
    this.relayReady = false;
    // The same flag `closeStream` clears, for the same reason: with no stream a
    // frame that still reaches `sendAudio` is dropped, never held.
    this.relayRequested = false;
    // Removed before anything else can arrive on them. NOT a disconnect: this
    // socket belongs to the whole app, and other features are using it.
    this.detachRelayListeners();
    this.preBuffer = [];
    this.audioChunks = [];
    this.audioSamples = 0;

    try {
      if (this.workletNode) {
        this.workletNode.port.onmessage = null;
        this.workletNode.disconnect();
      }
    } catch {
      /* already torn down */
    }
    try {
      if (this.sourceNode) this.sourceNode.disconnect();
    } catch {
      /* already torn down */
    }
    this.releaseMicrophone();
    try {
      if (this.audioContext && this.audioContext.state != "closed") {
        this.audioContext.close();
      }
    } catch {
      /* already torn down */
    }

    this.onEvent("closed", {
      reason,
      seconds,
      billedSeconds,
      spokenSeconds,
      outcome: this.outcome,
      kind,
      totalMs,
    });
  }
}
